import { create } from 'zustand/react';
import dayjs from 'dayjs';
import {
  AcceptedColumn,
  ImportAgreementPricesState,
  MgItemPurchasePrice,
  RejectedRow,
} from './types';
import {
  getIsRecognizedHeaderPredicate,
  gtinRegex,
  recognizedPurchasePriceHeadings,
} from './utils';
import {
  recognizedMgItemNrHeadings,
  recognizedGtinHeadings,
  recognizedValidFromHeadings,
  recognizedValidToHeadings,
  recognizedFinfoNrHeadings,
  recognizedNobbNrHeadings,
} from '@retail/utils';

export const useImportAgreementPricesStore = create<ImportAgreementPricesState>((set) => ({
  importedItemPrices: undefined,
  rejectedRows: undefined,
  importedFile: undefined,

  importSpreadsheet: (rows, file, countryCode) => {
    const [headerRow, ...dataRows] = rows;

    const mgItemNumberColumnIndex = headerRow.findIndex((header) =>
      recognizedMgItemNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const priceColumnIndex = headerRow.findIndex((header) =>
      recognizedPurchasePriceHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const validFromColumnIndex = headerRow.findIndex((header) =>
      recognizedValidFromHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const validToColumnIndex = headerRow.findIndex((header) =>
      recognizedValidToHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const gtinColumnIndex = headerRow.findIndex((header) =>
      recognizedGtinHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const finfoColumnIndex = headerRow.findIndex((header) =>
      recognizedFinfoNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const nobbColumnIndex = headerRow.findIndex((header) =>
      recognizedNobbNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );

    const updatedState = dataRows.reduce(
      (tmpResult, row, index) => {
        const rejectedColumns: AcceptedColumn[] = [];
        const mgItemNumber =
          mgItemNumberColumnIndex !== -1 && !!row.at(mgItemNumberColumnIndex)
            ? Number(row.at(mgItemNumberColumnIndex))
            : undefined;
        const finfoNumber =
          countryCode === 'SE' && finfoColumnIndex !== -1 && !!row.at(finfoColumnIndex)
            ? Number(row.at(finfoColumnIndex))
            : undefined;
        const nobbNumber =
          countryCode === 'NO' && nobbColumnIndex !== -1 && !!row.at(nobbColumnIndex)
            ? Number(row.at(nobbColumnIndex))
            : undefined;
        const price = row.at(priceColumnIndex)
          ? Number(`${row.at(priceColumnIndex)}`?.replace(',', '.'))
          : NaN;

        const validFrom =
          validFromColumnIndex !== -1 && row.at(validFromColumnIndex) !== undefined
            ? dayjs(`${row.at(validFromColumnIndex)}`, 'YYYY-MM-DD')
            : dayjs(new Date(), 'YYYY-MM-DD');
        const validToValue = validToColumnIndex !== -1 ? row.at(validToColumnIndex) : undefined;
        const validTo = validToValue ? dayjs(`${validToValue}`) : undefined;
        const gtinValue = row.at(gtinColumnIndex);

        if (
          !mgItemNumber &&
          ((countryCode === 'SE' && !finfoNumber) || (countryCode === 'NO' && !nobbNumber))
        ) {
          rejectedColumns.push('mgItem');
          if (countryCode === 'NO') {
            rejectedColumns.push('nobb');
          } else {
            rejectedColumns.push('finfo');
          }
        }

        if (Number.isNaN(price)) rejectedColumns.push('purchasePrice');
        if (!validFrom.isValid()) rejectedColumns.push('validFrom');
        if (validTo && !validTo.isValid()) rejectedColumns.push('validTo');
        if (gtinValue && !gtinRegex.test(`${gtinValue}`)) rejectedColumns.push('gtin');

        if (rejectedColumns.length) {
          tmpResult.rejectedRows.push({ columns: rejectedColumns, index });
        } else {
          tmpResult.importedItemPrices.push({
            mgItemNumber: mgItemNumber,
            finfoNumber: finfoNumber,
            nobbNumber,
            price,
            validFrom: validFrom.format('YYYY-MM-DD'),
            validTo: validTo?.format('YYYY-MM-DD'),
            gtinCode: gtinValue ? Number(gtinValue) : undefined,
          });
        }
        return tmpResult;
      },
      { importedItemPrices: [], rejectedRows: [] } as {
        importedItemPrices: MgItemPurchasePrice[];
        rejectedRows: RejectedRow[];
      }
    );

    set({ ...updatedState, importedFile: file });
  },

  clearState: () =>
    set({ importedItemPrices: undefined, rejectedRows: undefined, importedFile: undefined }),
}));
